import React, { useEffect, useState } from "react";
import { Input, Checkbox, message, Modal, Form, Button, Select } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import "./login.css";
import drEasyApi, { setApiSlug } from "../../../Api/Customapi";
import { HiOutlineMail, HiOutlineUser } from "react-icons/hi";
import { GiPadlock, GiPhone } from "react-icons/gi";
import ForgotPasswordModal from "../../Modals/ForgotPasswordModal";
import VerificationModal from "../../Modals/VerificationModal";
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google";
import { useLanguage } from "../../../context/LanguageContext";
import { Radio } from "antd";
import LanguageSwitcher from "../../Ui/LanguageSwitcher";
import i18n from "../../../i18n";
import { slug } from "../../../utils/store";

export default function AuthForm({ active }) {
  const [loginForm] = Form.useForm();
  const [signupForm] = Form.useForm();
  const { t, isRTL } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [shake, setShake] = useState(false);
  const [activeTab, setActiveTab] = useState(active ?? "login");

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStep, setForgotStep] = useState(1);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Store signup token for auto-login after verification
  const [signupToken, setSignupToken] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.removeItem("docTkn");
  }, []);

  useEffect(() => {
    setApiSlug(slug);
  }, [slug]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Clear forms when switching tabs
  useEffect(() => {
    loginForm.resetFields();
    signupForm.resetFields();
  }, [activeTab, loginForm, signupForm]);
  const { Option } = Select;

  const handleLogin = async (values) => {
    setLoading(true);
    try {

      const endpoint = `/api/${slug}/User/CenterLogin`;
      const payload = { email: values.email, password: values.password };

      const response = await drEasyApi.post(endpoint, payload);

      const { token, role } = response.data;

      if (role !== 1) {
        message.error(t("auth.unauthorizedAccess"));
        return;
      }

      // Store token based on remember me option
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem(`userTkn-${slug}`, token);

      message.success(t("auth.loginSuccess"));
      navigate(`/home`);
    } catch (error) {
      triggerErrorAnimation();

      if (error.response?.status === 401 || error.response?.status === 400) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.[0] ||
          "";

        // Check if error is related to verification
        if (errorMessage.toLowerCase().includes("verify")) {
          message.error(t("auth.verifyEmail"));
          setVerificationEmail(values.email);
          setShowVerificationModal(true);
          setResendCooldown(60);
        } else if (errorMessage.toLowerCase().includes("login invalid")) {
          message.error(t("auth.invalidCredentials"));
        } else {
          message.error(errorMessage);
        }
      } else if (error.response?.data?.errors) {
        message.error(error.response.data.errors[0]);
      } else {
        message.error(t("auth.loginFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (values) => {
    setLoading(true);
    try {

      const endpoint = `/api/${slug}/User/RegisterAsCenterUser`;
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        gender: values.gender,
        password: values.password,
        birthDay: `${values.dobYear}-${String(values.dobMonth).padStart(2, "0")}-${String(values.dobDay).padStart(2, "0")}`,
      };

      const response = await drEasyApi.post(endpoint, payload);

      const { token } = response.data;
      setSignupToken(token);

      message.success(t("auth.registrationSuccess"));
      setVerificationEmail(values.email);
      setShowVerificationModal(true);
      setResendCooldown(60);

      signupForm.resetFields();
    } catch (error) {
      triggerErrorAnimation();

      if (error.response?.status === 401 || error.response?.status === 400) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.[0] ||
          "";
        message.error(errorMessage);
      } else if (error.response?.data?.errors) {
        message.error(error.response.data.errors[0]);
      } else {
        message.error(t("auth.registrationFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      message.warning(t("auth.enterVerificationCode"));
      return;
    }

    setVerificationLoading(true);
    try {
      const res = await drEasyApi.post(`/api/${slug}/User/Verify`, {
        email: verificationEmail,
        otpCode: verificationCode,
      });
      const { token, role } = res.data;

      message.success(t("auth.verificationSuccess"));

      // Auto-login after successful verification
      if (token) {
        // Store the token
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(`userTkn-${slug}`, token);

        navigate(`/${slug}/home`);
      } else {
        setShowVerificationModal(false);
        setActiveTab("login");
      }

      setVerificationCode("");
      setSignupToken("");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0] ||
        t("auth.verificationFailed");
      message.error(errorMessage);
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await drEasyApi.post(`/api/${slug}/User/ResendOtp`, {
        email: verificationEmail,
      });

      message.success(t("auth.codeSent"));
      setResendCooldown(60);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0] ||
        t("auth.resendFailed");
      message.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const triggerErrorAnimation = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const handleGoogleSignIn = async (response) => {
    try {

      // Send the Google ID token to your backend
      const apiResponse = await drEasyApi.post(`/api/${slug}/User/GoogleAuth`, {
        token: response.credential, // This is the ID token (JWT)
      });


      const { token, role } = apiResponse.data;

      // Validate if the role is 1 (user)
      if (role !== 1) {
        message.error(t("auth.unauthorizedAccess"));
        return;
      }

      // Store token based on remember me option
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem(`userTkn-${slug}`, token);

      message.success(t("auth.googleSignInSuccess"));
      navigate(`/${slug}/home`);
    } catch (error) {
      console.error("Google Sign-In error:", error);

      if (error.response?.status === 401 || error.response?.status === 400) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.[0] ||
          "";
        message.error(errorMessage || t("auth.googleSignInFailed"));
      } else if (error.response?.data?.errors) {
        message.error(error.response.data.errors[0]);
      } else {
        message.error(t("auth.googleSignInFailed"));
      }
    }
  };

  const handleResendResetCode = async () => {
    try {
      await drEasyApi.post(`/api/${slug}/User/ForgotPassword`, {
        email: forgotEmail,
      });
      message.success(t("auth.resetCodeSent"));
    } catch (err) {
      if (err.response?.data.errors[0].includes("not found")) {
        message.error(t("auth.emailNotFound"));
      } else {
        message.error(
          err.response?.data.errors[0] || t("auth.sendResetEmailFailed"),
        );
      }
      throw err;
    }
  };

  const renderLoginForm = () => (
    <Form
      form={loginForm}
      layout="vertical"
      onFinish={handleLogin}
      className="space-y-4"
      size="large"
    >
      <Form.Item
        name="email"
        label={
          <span className="text-textPrimary text-lg">{t("auth.email")}</span>
        }
        rules={[
          {
            required: true,
            message: t("auth.emailRequired"),
          },
          {
            type: "email",
            message: t("auth.emailInvalid"),
          },
        ]}
      >
        <Input
          placeholder={t("auth.emailPlaceholder")}
          prefix={<HiOutlineMail className="text-placeHolder" />}
          className="text-placeHolder placeholder:text-placeHolder"
        />
      </Form.Item>

      <Form.Item
        name="password"
        label={
          <span className="text-textPrimary text-lg">{t("auth.password")}</span>
        }
        rules={[
          {
            required: true,
            message: t("auth.passwordRequired"),
          },
        ]}
      >
        <Input.Password
          placeholder={t("auth.passwordPlaceholder")}
          prefix={<GiPadlock className="text-placeHolder" />}
          className="text-placeHolder placeholder:text-placeHolder"
        />
      </Form.Item>

      <div className="flex items-center justify-between">
        <Checkbox
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="text-primary text-lg"
        >
          {t("auth.rememberMe")}
        </Checkbox>
        <span
          className="text-secondary text-lg cursor-pointer hover:underline"
          onClick={() => {
            setForgotStep(1);
            setForgotEmail("");
            setResetCode("");
            setNewPassword("");
            setShowForgotModal(true);
            setResendCooldown(60);
          }}
        >
          {t("auth.forgotPassword")}
        </span>
      </div>

      <Button
        type=""
        htmlType="submit"
        loading={loading}
        className="w-full py-3 h-auto text-lg text-white font-semibold bg-primary hover:bg-primary_hover border-none"
      >
        {loading ? t("auth.loggingIn") : t("auth.confirmLogin")}
      </Button>
    </Form>
  );

 const renderSignupForm = () => (
  <Form
    form={signupForm}
    layout="vertical"
    onFinish={handleSignup}
    className="space-y-4"
    size="large"
  >
    {/* Row 1: Name */}
    <div className="flex gap-4">
      <Form.Item
        name="firstName"
        label={<span className="text-textPrimary text-lg">{t("auth.firstName")}</span>}
        className="flex-1 mb-0"
        rules={[
          { required: true, message: t("auth.firstNameRequired") },
          { min: 2, message: t("auth.firstNameMin") },
        ]}
      >
        <Input
          placeholder={t("auth.firstNamePlaceholder")}
          prefix={<HiOutlineUser className="text-placeHolder" />}
        />
      </Form.Item>

      <Form.Item
        name="lastName"
        label={<span className="text-textPrimary text-lg">{t("auth.lastName")}</span>}
        className="flex-1 mb-0"
        rules={[
          { required: true, message: t("auth.lastNameRequired") },
          { min: 2, message: t("auth.lastNameMin") },
        ]}
      >
        <Input
          placeholder={t("auth.lastNamePlaceholder")}
          prefix={<HiOutlineUser className="text-placeHolder" />}
        />
      </Form.Item>
    </div>

    {/* Row 2: Email & Phone */}
    <div className="flex gap-4">
      <Form.Item
        name="email"
        label={<span className="text-textPrimary text-lg">{t("auth.email")}</span>}
        className="flex-1 mb-0"
        rules={[
          { required: true, message: t("auth.emailRequired") },
          { type: "email", message: t("auth.emailInvalid") },
        ]}
      >
        <Input
          placeholder={t("auth.emailAddress")}
          prefix={<HiOutlineMail className="text-placeHolder" />}
        />
      </Form.Item>

      <Form.Item
        name="phoneNumber"
        label={<span className="text-textPrimary text-lg">{t("auth.phoneNumber")}</span>}
        className="flex-1 mb-0"
        rules={[
          { required: true, message: t("auth.phoneNumberRequired") },
          { pattern: /^[\+]?[0-9][\d]{0,15}$/, message: t("auth.phoneNumberInvalid") },
        ]}
      >
        <Input
          placeholder={t("auth.phoneNumberPlaceholder")}
          prefix={<GiPhone className={`text-placeHolder ${isRTL ? "rotate-180" : ""}`} />}
        />
      </Form.Item>
    </div>

      <Form.Item
        name="gender"
        label={<span className="text-textPrimary text-lg">{t("auth.gender")}</span>}
        className="w-full mb-0"
        rules={[{ required: true, message: t("auth.genderRequired") }]}
      >
        <Select placeholder={t("auth.genderPlaceholder")} className="gender-select">
          <Select.Option value={0}>{t("auth.male")}</Select.Option>
          <Select.Option value={1}>{t("auth.female")}</Select.Option>
        </Select>
      </Form.Item>
    <div className="flex gap-4">

      <Form.Item
        name="dobDay"
        label={<span className="text-textPrimary text-lg">{t("auth.dob")}</span>}
        className="flex-1 mb-0"
        rules={[{ required: true, message: isRTL ? "اليوم مطلوب" : "Day is required" }]}
      >
        <Select placeholder="Day">
          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
            <Select.Option key={day} value={day}>{day}</Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="dobMonth"
        label=" "
        className="flex-1 mb-0"
      >
        <Select placeholder="Month">
          {["January","February","March","April","May","June",
            "July","August","September","October","November","December",
          ].map((month, index) => (
            <Select.Option key={index + 1} value={index + 1}>{month}</Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="dobYear"
        label=" "
        className="flex-1 mb-0"
      >
        <Select placeholder="Year" showSearch>
          {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((year) => (
            <Select.Option key={year} value={year}>{year}</Select.Option>
          ))}
        </Select>
      </Form.Item>
    </div>

    {/* Row 4: Password & Confirm Password */}
    <div className="flex gap-4">
      <Form.Item
        name="password"
        label={<span className="text-textPrimary text-lg">{t("auth.password")}</span>}
        className="flex-1 mb-0"
        rules={[
          { required: true, message: t("auth.passwordCreate") },
          { min: 6, message: t("auth.passwordMinLength") },
          {
              pattern: /^(?=.*[A-Z]*[a-z])(?=.*\d)[A-Za-z\d@$!%*#?&.]{6,}$/,
            message: t("auth.passwordPattern"),
          },
        ]}
      >
        <Input.Password
          placeholder={t("auth.createPassword")}
          prefix={<GiPadlock className="text-placeHolder" />}
        />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label={<span className="text-textPrimary text-lg">{t("auth.confirmPassword")}</span>}
        className="flex-1 mb-0"
        dependencies={["password"]}
        rules={[
          { required: true, message: t("auth.confirmPasswordRequired") },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) return Promise.resolve();
              return Promise.reject(new Error(t("auth.passwordsMismatch")));
            },
          }),
        ]}
      >
        <Input.Password
          placeholder={t("auth.confirmPasswordPlaceholder")}
          prefix={<GiPadlock className="text-placeHolder" />}
        />
      </Form.Item>
    </div>

    <Button
      htmlType="submit"
      loading={loading}
      className="w-full py-3 h-auto text-white text-lg font-semibold bg-primary hover:bg-primary_hover border-none"
    >
      {loading ? t("auth.registering") : t("auth.createAccountBtn")}
    </Button>
  </Form>
);

  return (
    <div className="min-h-screen flex flex-col sm:flex-row sm:p-0 p-5 gap-y-3">
      {/* Left Side - Image + Welcome */}
<div className="w-full sm:w-1/2 bg-primary min-h-screen sm:rounded-none rounded-full aspect-square text-white sm:p-10 flex flex-col justify-center items-center sm:py-10">
        <h1 className="text-3xl font-bold text-secondary sm:inline-block hidden">
          {activeTab === "login"
            ? t("auth.welcomeBack")
            : t("auth.createAccount")}
        </h1>
        <p className="text-lg text-page text-center sm:inline-block hidden">
          {activeTab === "login"
            ? t("auth.accessPortal")
            : t("auth.joinCommunity")}
        </p>
        <img
          src={require("../../../assets/login-illustration.png")}
          alt="Medical Illustration"
          className=" h-screen"
        />
      </div>

      {/* Right Side - Auth Form */}
<div className="w-full sm:w-1/2 2xl:max-w-3xl 2xl:mx-auto sm:p-10 flex flex-col items-center justify-center">
        <LanguageSwitcher />

        <div className="flex justify-center my-10 w-full">
          <div className="flex border border-primary rounded-xl overflow-hidden shadow-lg w-full">
            <button
              onClick={() => setActiveTab("login")}
              className={`w-1/2 py-2 font-semibold text-lg transition duration-300 ${
                activeTab === "login"
                  ? "bg-primary text-white"
                  : "text-primary bg-transparent"
              }`}
            >
              {t("auth.login")}
            </button>
            <button
              onClick={() => setActiveTab("signup")}
              className={`w-1/2 py-2 font-semibold text-lg transition duration-300 ${
                activeTab === "signup"
                  ? "bg-primary text-white"
                  : "text-primary bg-transparent"
              }`}
            >
              {t("auth.signup")}
            </button>
          </div>
        </div>

        <div className={`w-full ${shake ? "shake" : ""}`}>
          {activeTab === "login" ? renderLoginForm() : renderSignupForm()}

          <div className="flex items-center justify-center my-4">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="px-4 text-gray-500 text-sm font-medium">
              {t("auth.or")}
            </span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          <div className="flex justify-center items-center w-full">
            <GoogleLogin
              onSuccess={handleGoogleSignIn}
              onError={(error) => {
                message.error(t("auth.googleSignInFailed"));
              }}
              size="large"
              width="100%"
              text="continue_with"
              shape="rectangular"
              locale="ar"
            />
          </div>
        </div>
      </div>

      <VerificationModal
        open={showVerificationModal}
        onClose={() => {
          setShowVerificationModal(false);
          setSignupToken("");
        }}
        email={verificationEmail}
        code={verificationCode}
        setCode={setVerificationCode}
        loading={verificationLoading}
        onVerify={handleVerifyCode}
        onResend={handleResendCode}
        resendLoading={resendLoading}
        resendCooldown={resendCooldown}
      />

      <ForgotPasswordModal
        open={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        step={forgotStep}
        email={forgotEmail}
        setEmail={setForgotEmail}
        code={resetCode}
        setCode={setResetCode}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        loading={resetLoading}
        onSendResetCode={async () => {
          if (!forgotEmail.trim())
            return message.warning(t("auth.emailRequired"));
          setResetLoading(true);
          try {
            await drEasyApi.post(`/api/${slug}/User/ForgotPassword`, {
              email: forgotEmail,
            });
            message.success(t("auth.resetCodeSent"));
            setForgotStep(2);
          } catch (err) {
            if (err.response?.data.errors[0].includes("not found")) {
              message.error(t("auth.emailNotFound"));
            } else {
              message.error(
                err.response?.data.errors[0] || t("auth.sendResetEmailFailed"),
              );
            }
          } finally {
            setResetLoading(false);
          }
        }}
        onResendCode={handleResendResetCode}
        onResetPassword={async () => {
          if (!resetCode.trim() || !newPassword.trim()) {
            return message.warning(t("auth.fillAllFields"));
          }
          setResetLoading(true);
          try {
            await drEasyApi.post(`/api/${slug}/User/ResetPassword`, {
              email: forgotEmail,
              resetCode,
              newPassword,
            });
            message.success(t("auth.passwordResetSuccess"));
            setShowForgotModal(false);
            setActiveTab("login");
          } catch (err) {
            const error =
              err.response?.data?.errors?.[0] || err.response?.data?.message;

            if (!error) {
              message.error(t("auth.passwordResetFailed"));
            } else if (error.includes("Invalid")) {
              message.error(t("auth.invalidVerificationCode"));
            } else if (error.toLowerCase().includes("at least 6")) {
              message.error(t("auth.passwordMinRequirement"));
            } else if (error.toLowerCase().includes("password")) {
              message.error(error);
            } else {
              message.error(error);
            }
          } finally {
            setResetLoading(false);
          }
        }}
      />
    </div>
  );
}
